import { Alert, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useRulesForm from '../useRulesForm';
import RulesFields from './RulesFields';
import SaveButton from './SaveButton';

/**
 * The channel's default GoalRush — the one that always runs. Saving it the first time puts
 * GoalRush on the channel: its default session starts at once and the app shows it. After that
 * the three parts save with one button, and every change applies from the next session (the
 * running one keeps its rules). Backend: PUT /admin/goalrush/setConfig.
 */
const DefaultsSection = ({ teamId, config, onConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useRulesForm(config.resolved);
  // Not set up yet: the first save may keep every default as it is.
  const firstSave = !config.resolved?.enabled;
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const save = async () => {
    setConfirming(false);
    setSaving(true);
    const res = await Api.put('/admin/goalrush/setConfig', { teamId, rules: form.patch }, null, {
      silent: true
    });
    setSaving(false);
    if (res?.data) {
      onConfig(res.data);
      form.markSaved(res.data.resolved);
      dispatch({ type: 'HANDLE_RES', payload: { res: t('admin.goalRush.saved') } });
    } else {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: res?.err || t('admin.goalRush.saveFailed') }
      });
    }
  };

  return (
    <>
      {firstSave && (
        <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: '12.5px' }}>
          {t('admin.goalRush.defaults.notStarted')}
        </Text>
      )}
      <RulesFields
        draft={form.draft}
        errors={form.errors}
        onChange={form.onChange}
        sessionNote={
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
            {t('admin.goalRush.defaults.alwaysOn')}
          </Text>
        }
      />
      <SaveButton
        saving={saving}
        disabled={(!firstSave && !form.dirty) || !form.valid}
        onClick={() => setConfirming(true)}
      />
      {confirming && (
        <Alert
          text={
            firstSave
              ? t('admin.goalRush.defaults.confirmFirstSave')
              : t('admin.goalRush.defaults.confirmSave')
          }
          acceptText={t('admin.goalRush.confirm')}
          refuseText={t('admin.goalRush.cancel')}
          accept={save}
          refuse={() => setConfirming(false)}
        />
      )}
    </>
  );
};

export default DefaultsSection;
